import { useState, useEffect } from 'react'
import { fecharCaixa, getTurnoCaixaAtual, getMovimentacoes, type TurnoCaixa, type ReciboTurno } from '../api/client'
import { imprimirReciboFechamentoCaixa } from '../utils/impressao'

/**
 * FecharCaixaModal — Fechamento de turno estilo PDV (D-138, 2026-05-18).
 *
 * Mudanças vs D-102/D-112:
 *   - Removido campo "venda em dinheiro" (D-138: dinheiro não é forma de
 *     pagamento; vendas vêm 100% por débito/crédito/PIX). Operador só conta
 *     o FUNDO DE TROCO físico.
 *   - Esperado em caixa = inicial + suprimentos − sangrias
 *   - Diferença ≠ 0 EXIGE motivo (validado no backend; UI cobra antes do POST)
 *   - Recibo Z numerado (z_numero_sequencial)
 *
 * Fluxo:
 *   1. Abre, lê /caixa/atual (preview vendas + Z-number) e /movimentacoes (sangrias/suprimentos)
 *   2. Input "Valor contado em dinheiro físico"
 *   3. Se diferença ≠ 0 → motivo obrigatório (renderização condicional)
 *   4. Confirma → POST /caixa/fechar → recibo Z → imprime
 */
export default function FecharCaixaModal({
  open,
  onClose,
  onFechado,
}: {
  open: boolean
  onClose: () => void
  onFechado: (recibo: ReciboTurno) => void
}) {
  const [turno, setTurno] = useState<TurnoCaixa | null>(null)
  const [valorFinalTexto, setValorFinalTexto] = useState('')
  const [diferencaMotivo, setDiferencaMotivo] = useState('')
  const [observacao, setObservacao] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [movTotais, setMovTotais] = useState({ sangrias: 0, suprimentos: 0 })

  useEffect(() => {
    if (!open) return
    setError(''); setLoading(true)
    setValorFinalTexto(''); setDiferencaMotivo(''); setObservacao('')
    Promise.all([
      getTurnoCaixaAtual(),
      getMovimentacoes().catch(() => ({ movimentacoes: [], totais: { sangrias: 0, suprimentos: 0, saldo_liquido: 0 } })),
    ])
      .then(([r, mov]) => {
        if (!r.aberto) { setError('Não há turno aberto.'); setTurno(null) }
        else setTurno(r.turno)
        setMovTotais({ sangrias: mov.totais.sangrias, suprimentos: mov.totais.suprimentos })
      })
      .catch(e => setError(e?.message || 'Erro ao consultar turno'))
      .finally(() => setLoading(false))
  }, [open])

  if (!open) return null

  function parseValorCentavos(s: string): number | null {
    if (!s.trim()) return 0
    const norm = s.trim().replace(/\./g, '').replace(',', '.')
    const num = Number(norm)
    if (!Number.isFinite(num) || num < 0) return null
    return Math.round(num * 100)
  }

  const fmtBRL = (centavos: number) => `R$ ${(centavos / 100).toFixed(2).replace('.', ',')}`
  const previewTotal = Number(turno?.total_vendido_preview_centavos ?? 0)
  const valorInicial = Number(turno?.valor_inicial ?? 0)
  // D-138: fórmula PDV — esperado = inicial + suprimentos − sangrias
  // (sem somar vendas, pois não há venda em dinheiro)
  const esperado = valorInicial + movTotais.suprimentos - movTotais.sangrias
  const contado = parseValorCentavos(valorFinalTexto) ?? 0
  const diferenca = contado - esperado
  const exigeMotivo = valorFinalTexto.trim().length > 0 && diferenca !== 0

  async function handleFechar() {
    setError('')
    const centavos = parseValorCentavos(valorFinalTexto)
    if (centavos === null) {
      setError('Valor contado inválido.')
      return
    }
    // D-138: motivo obrigatório se diferença ≠ 0
    if (centavos - esperado !== 0 && diferencaMotivo.trim().length === 0) {
      setError('Diferença detectada — informe o motivo antes de fechar.')
      return
    }
    setSubmitting(true)
    try {
      // venda_dinheiro_centavos = 0 (PDV novo não usa dinheiro como forma)
      const r = await fecharCaixa(centavos, observacao || undefined, 0, diferencaMotivo || undefined)
      try { await imprimirReciboFechamentoCaixa(r.recibo) } catch { /* printer offline OK */ }
      onFechado(r.recibo)
    } catch (e: any) {
      setError(e?.message || 'Erro ao fechar caixa')
    } finally {
      setSubmitting(false)
    }
  }

  const zNumero = turno?.z_numero_sequencial

  return (
    <div className="fixed inset-0 z-50 bg-carvao/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-modal rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-white/40">
          <p className="text-[10px] uppercase tracking-[0.18em] text-terracota/80 font-semibold">
            PDV · Fechamento Z
          </p>
          <h2 className="font-serif text-xl font-semibold text-carvao mt-1">
            Fechar Caixa {zNumero ? <span className="text-terracota">#{zNumero}</span> : null}
          </h2>
          <p className="text-xs text-gray-500 mt-1">Confira valores e imprima o Z.</p>
        </div>

        <div className="p-5">
          {loading && <p className="text-sm text-gray-400">Carregando turno…</p>}

          {turno && (
            <>
              {/* Vendas no turno (resumo) */}
              <div className="bg-cream-dark/30 rounded-lg p-3 mb-3 space-y-1.5 text-sm">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Vendas no turno</p>
                <div className="flex justify-between">
                  <span className="text-gray-700">Total vendido (sistema)</span>
                  <span className="font-mono tabular-nums text-carvao font-medium">{fmtBRL(previewTotal)}</span>
                </div>
                <p className="text-[10px] text-gray-400 leading-snug">
                  Vendas por débito / crédito / PIX. Forma de pagamento por transação será preenchida em atualização futura.
                </p>
              </div>

              {/* Fundo de troco físico */}
              <div className="bg-white/50 border border-white/40 rounded-lg p-3 mb-4 space-y-1.5 text-sm">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Fundo de troco (dinheiro físico)</p>
                <div className="flex justify-between">
                  <span className="text-gray-600">Abertura</span>
                  <span className="font-mono tabular-nums">{fmtBRL(valorInicial)}</span>
                </div>
                {movTotais.suprimentos > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>+ Suprimentos</span>
                    <span className="font-mono tabular-nums">+ {fmtBRL(movTotais.suprimentos)}</span>
                  </div>
                )}
                {movTotais.sangrias > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>− Sangrias</span>
                    <span className="font-mono tabular-nums">− {fmtBRL(movTotais.sangrias)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-200/70 pt-1.5 mt-1.5 font-medium">
                  <span>Esperado em caixa</span>
                  <span className="font-mono tabular-nums text-terracota">{fmtBRL(esperado)}</span>
                </div>
              </div>

              {/* Contado físico */}
              <div className="mb-3">
                <label className="block text-xs text-gray-600 mb-1.5 font-medium">
                  Valor contado em dinheiro (físico)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">R$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={valorFinalTexto}
                    onChange={e => setValorFinalTexto(e.target.value)}
                    placeholder="0,00"
                    disabled={submitting}
                    autoFocus
                    className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 text-base font-mono tabular-nums focus:border-terracota focus:outline-none bg-white/70"
                  />
                </div>
                {valorFinalTexto && (
                  <p className={`text-xs mt-1.5 font-medium ${diferenca === 0 ? 'text-gray-500' : diferenca > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    Diferença: {diferenca >= 0 ? '+' : ''}{fmtBRL(Math.abs(diferenca))} {diferenca > 0 ? '(sobra)' : diferenca < 0 ? '(falta)' : '(igual)'}
                  </p>
                )}
              </div>

              {/* Motivo obrigatório quando diferença ≠ 0 */}
              {exigeMotivo && (
                <div className="mb-3">
                  <label className="block text-xs text-amber-800 mb-1.5 font-semibold">
                    Motivo da diferença <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={diferencaMotivo}
                    onChange={e => setDiferencaMotivo(e.target.value)}
                    placeholder="Ex: troco devolvido a mais, vale não registrado..."
                    disabled={submitting}
                    className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:outline-none bg-amber-50/60"
                    maxLength={500}
                  />
                </div>
              )}

              {/* Observação opcional */}
              <div className="mb-4">
                <label className="block text-xs text-gray-600 mb-1.5 font-medium">Observação (opcional)</label>
                <textarea
                  rows={2}
                  value={observacao}
                  onChange={e => setObservacao(e.target.value)}
                  placeholder="Ex: troco passado pro turno seguinte, notas grandes..."
                  disabled={submitting}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-terracota focus:outline-none resize-none bg-white/70"
                  maxLength={500}
                />
              </div>
            </>
          )}

          {error && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-3 mb-3 text-sm text-red-800">{error}</div>
          )}

          <div className="flex gap-2">
            <button onClick={onClose} disabled={submitting} className="btn btn-secondary flex-1">Cancelar</button>
            <button onClick={handleFechar} disabled={submitting || !turno} className="btn btn-primary flex-1">
              {submitting ? 'Fechando…' : 'Fechar Turno Z'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
